import { isIntersectionObserverAvailable } from '@/common/helpers';
import { StorageService } from '@/services/storage';
import { I18n } from '@/precise-price/i18n';
import { Locales } from '@/locales';
import { WebsiteService } from '@/services/website';
import { captureException, setTag } from '@sentry/browser';

export class CoreService {
	private warmupQueue: string[] = [];

	// eslint-disable-next-line no-useless-constructor
	constructor(private website: WebsiteService, private storage: StorageService, private i18n: I18n) {
	}

	public async loadLocale(locale = Locales.EN) {
		try {
			const translations = await this.website.getTranslations();

			this.i18n.global.mergeLocaleMessage(locale, translations);
		} catch (e) {
			console.error('Get translations error', e);

			//send error to Sentry
			setTag('info', 'translationsError');
			captureException(e);
		}
	}

	public async changeLocale(locale: Locales) {
		try {
			const translations = await this.website.getTranslations(locale);

			// TODO: cache already loaded translations
			this.i18n.global.mergeLocaleMessage(locale, translations);
			this.i18n.global.locale.value = locale;
		} catch (e) {
			console.error('Get translations error', e);

			//send error to Sentry
			setTag('info', 'translationsError');
			captureException(e);
		}
	}

	public warmupInViewport(element: Element, vin: string) {
		if (!isIntersectionObserverAvailable()) { return; }

		const callback = (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
			entries.forEach(entry => {
				if (entry.isIntersecting) {
					this.warmup(vin).catch();
					observer.unobserve(entry.target);
				}
			});
		};
		const observer = new IntersectionObserver(callback);

		observer.observe(element as Element);
	}

	public warmup(vin: string): Promise<void> {
		if (this.detectBot() || this.storage.isWarmedUp(vin) || this.warmupQueue.includes(vin)) {
			return Promise.resolve();
		}

		// prevent several requests for the same vin in case of multiple different widgets on the page
		this.warmupQueue.push(vin);

		return this.website.warmupRequest(vin)
			.then(() => {
				this.storage.setWarmedUp(vin);
			}).catch(e => {
				console.error('Warmup request error', e);
				// TODO: send error to Sentry
				//send error to Sentry
				setTag('info', 'WarmUpError');
				captureException(e);
			});
	}

	private detectBot(): boolean {
		return /bot|google|baidu|bing|msn|duckduckbot|teoma|slurp|yandex/i.test(navigator.userAgent);
	}
}
